import { BOOKING_API_URL, supabase } from '../lib/supabase';

export interface EditSlotLeg {
  room_id: string;
  slot_start: string;
  party_size: number;
}

export interface EditSlotsRequest {
  action: 'slots';
  booking_id: string;
  new_party_size: number;
  new_slots: EditSlotLeg[];
  force?: boolean;
  /** Force-edit an already-arrived booking. Default false. */
  force_arrived?: boolean;
  reason?: string | null;
}

// Time-only move of a multi-pick departure. booking_id is the departure_id;
// every pick shifts to new_slot_start keeping its experience / room / party
// size. force bypasses capacity; force_arrived moves an arrived departure.
export interface EditMoveRequest {
  action: 'move';
  booking_id: string;
  new_slot_start: string;
  force?: boolean;
  force_arrived?: boolean;
  reason?: string | null;
}

export interface EditCustomerRequest {
  action: 'customer';
  customer_id: string;
  // PARENT booking id: when given, the booking's own name snapshot
  // (guest_name) follows the corrected customer name.
  booking_id?: string;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  locale?: string | null;
  email?: string | null;
  reason?: string | null;
}

// Rename a booking's own name snapshot (the only name of a customer-less
// balie walk-in). booking_id is the PARENT booking id.
export interface EditGuestNameRequest {
  action: 'guest_name';
  booking_id: string;
  guest_name: string;
  reason?: string | null;
}

// Shrink a departure's party size and issue a tegoedbon for the removed seats.
// booking_id is the departure_id. Two variants:
//   • single-slot departure → set `new_party_size` (the new total, 1..29).
//   • one timeslot of a time-split / multi-pick departure → set `slot_id` (the
//     booking_slot to shrink) + `new_slot_party_size` (its new size, 0..29; 0
//     drops that leg). When slot_id is present, new_party_size is ignored.
export interface EditReduceRequest {
  action: 'reduce';
  booking_id: string;
  new_party_size?: number;       // 1..29, smaller than current (single-slot path)
  slot_id?: string;              // per-slot path: the booking_slot to shrink
  new_slot_party_size?: number;  // per-slot path: slot's new size, 0..29
  force_arrived?: boolean;
  reason?: string | null;
}

// Response payload for action='reduce'.
export interface EditReduceResponse {
  departure_id: string;
  booking_id: string;
  departure_number: string;
  old_party_size: number;
  new_party_size: number;
  old_price_cents: number;
  new_price_cents: number;
  removed_value_cents: number;
  voucher: {
    code: string;
    amount_cents: number;
    email_sent: boolean;
  } | null;
}

export type EditRequest =
  | EditSlotsRequest
  | EditMoveRequest
  | EditReduceRequest
  | EditCustomerRequest
  | EditGuestNameRequest;

export interface EditError {
  error: string;
  message?: string;
}

export interface EditResult<T = unknown> {
  ok: boolean;
  data: T | null;
  error: EditError | null;
  status: number;
}

export async function editBooking<T = unknown>(
  body: EditRequest,
): Promise<EditResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: {
        error: 'network_error',
        message: e instanceof Error ? e.message : String(e),
      },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as EditError) ?? {
        error: 'unknown_error',
        message: `HTTP ${res.status}`,
      },
      status: res.status,
    };
  }

  return {
    ok: true,
    data: json as T,
    error: null,
    status: res.status,
  };
}
